import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import AuthCallback from "@/components/AuthCallback";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import MenuPage from "@/pages/MenuPage";
import Locations from "@/pages/Locations";
import Franchise from "@/pages/Franchise";
import Contact from "@/pages/Contact";
import Shop from "@/pages/Shop";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import PaymentResult from "@/pages/PaymentResult";
import Admin from "@/pages/Admin";
import Seo from "@/components/Seo";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ShopSoon = () => (
  <div className="min-h-screen bg-forest text-cream flex items-center justify-center px-5 grain relative" data-testid="shop-soon-page">
    <Seo
      title="Shop Coming Soon | Chai Times"
      description="The Chai Times online shop is coming soon. Visit a Bengaluru outlet for kulhad chai and café favourites."
      path="/shop"
      image="/kulhad-kesar.webp"
      noindex
    />
    <div className="text-center max-w-md">
      <img src="/logo.png" alt="Chai Times" className="w-24 h-24 mx-auto mb-8 rounded-full" />
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">The Shop</p>
      <h1 className="font-serif font-black text-4xl md:text-5xl">Still brewing.</h1>
      <p className="text-cream/70 mt-5 leading-relaxed">
        Our packaged blends and kulhad sets are getting their final polish. Visit any outlet for now — the online shop opens soon.
      </p>
      <a href="/menu" data-testid="shop-soon-menu-link" className="mt-9 inline-block rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-gold hover:text-charcoal transition-colors">
        Browse the Menu
      </a>
    </div>
  </div>
);

const Layout = ({ children }) => {
  const { pathname, hash } = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (hash?.includes("session_id=")) return <AuthCallback />;
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {isAdmin && <Seo title="Admin | Chai Times" description="Chai Times admin console." path="/admin" noindex />}
      {!isAdmin && <Navbar />}
      {children}
      {!isAdmin && <Footer />}
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/shop" element={<ShopSoon />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<JournalPost />} />
            <Route path="/payment/success" element={<PaymentResult mode="success" />} />
            <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Layout>
        <Toaster position="bottom-right" toastOptions={{ style: { background: "#1A1A1A", color: "#F5EDE3", border: "1px solid #C2703E" } }} />
      </BrowserRouter>
    </div>
  );
}

export default App;
